import axios from "axios";
import { API_ALL_CLIENTS_PORTFOLIO, API_CLIENTS_PORTFOLIO_SUMMARY } from "@/constants/api";

// Ответы на устаревшие запросы (пользователь успел кликнуть другую колонку) отбрасываются
let summaryRequestId = 0;
let tableRequestId = 0;

// Группа, которая открывается при входе на страницу
const DEFAULT_GROUP = "active";

// Параметры фильтров для всех запросов портфеля (график, таблица, экспорт)
function filterParams(state) {
  return {
    service_id: state.serviceId || undefined,
    date_from: state.dateFrom || undefined,
    date_to: state.dateTo || undefined,
    kam_ids: state.kamIds.length ? state.kamIds : undefined,
    sales_manager_ids: state.salesManagerIds.length ? state.salesManagerIds : undefined,
  };
}

function prepareRow(row) {
  return { ...row, ...row.order_ids, loading: false, loading_risk: false };
}

const clientPortfolio = {
  namespaced: true,

  state: {
    // Счётчики групп для графика: { potential: 10, new: 3, ... }
    summary: {},
    summaryStatus: "",

    // Фильтры
    serviceId: null,
    dateFrom: null,
    dateTo: null,
    kamIds: [],
    salesManagerIds: [],

    group: null,

    rows: [],
    total: 0,
    page: 1,
    perPage: 50,
    sortBy: "last_shipment_date",
    sortDesc: true,
    search: "",
    tableStatus: "",

    parent_subcompanies: '',
    subcompanies: [],
  },

  mutations: {
    SUMMARY_REQUEST(state) {
      state.summaryStatus = "loading";
    },
    SUMMARY_SUCCESS(state, payload) {
      state.summary = payload;
      state.summaryStatus = "success";
    },
    SUMMARY_ERROR(state) {
      state.summaryStatus = "error";
    },

    TABLE_REQUEST(state) {
      state.tableStatus = "loading";
    },
    TABLE_SUCCESS(state, { data, total, page }) {
      state.rows = data.map(prepareRow);
      state.total = total;
      state.page = page;
      state.tableStatus = "success";
    },
    TABLE_ERROR(state) {
      state.tableStatus = "error";
    },

    SET_SERVICE(state, payload) {
      state.serviceId = payload;
    },
    SET_FILTERS(state, { dateFrom, dateTo, kamIds, salesManagerIds }) {
      state.dateFrom = dateFrom || null;
      state.dateTo = dateTo || null;
      state.kamIds = kamIds || [];
      state.salesManagerIds = salesManagerIds || [];
    },
    SET_GROUP(state, payload) {
      state.group = payload;
    },
    SET_PAGE(state, payload) {
      state.page = payload;
    },
    SET_PER_PAGE(state, payload) {
      state.perPage = payload;
    },
    SET_SORT(state, { sortBy, sortDesc }) {
      state.sortBy = sortBy;
      state.sortDesc = sortDesc;
    },
    SET_SEARCH(state, payload) {
      state.search = payload;
    },
    CLEAR_TABLE(state) {
      state.rows = [];
      state.total = 0;
      state.page = 1;
    },

    SET_SUBCOMPANIES(state, payload) {
      state.parent_subcompanies = payload.name
      state.subcompanies = payload.subcompanies;
    },

    CLEAR_SUBCOMPANIES(state) {
      state.parent_subcompanies = ''
      state.subcompanies = [];
    },

    SET_RISK(state, { id, is_at_risk }) {
      const row = state.rows.find((item) => item.id === id);
      if (row) {
        row.is_at_risk = is_at_risk;
      }
    },
  },

  actions: {
    // Загрузка счётчиков и выбор группы: «Активные», если в них есть клиенты, иначе первая непустая.
    // keepGroup - при смене продукта оставить текущую группу, если в ней есть клиенты.
    async init({ state, commit, dispatch }, { keepGroup = false } = {}) {
      if (!keepGroup) {
        commit("SET_GROUP", null);
        commit("SET_SEARCH", "");
      }
      await dispatch("loadSummary");
      if (state.summaryStatus !== "success") {
        return;
      }

      const keys = Object.keys(state.summary);
      let group = keys.find((key) => state.summary[key] > 0) || keys[0] || null;
      if (state.summary[DEFAULT_GROUP] > 0) {
        group = DEFAULT_GROUP;
      }
      if (state.group && state.summary[state.group] > 0) {
        group = state.group;
      }

      commit("SET_GROUP", group);
      commit("SET_PAGE", 1);
      await dispatch("loadTable");
    },

    async loadSummary({ state, commit }) {
      const requestId = ++summaryRequestId;
      commit("SUMMARY_REQUEST");
      try {
        const { data } = await axios.get(API_CLIENTS_PORTFOLIO_SUMMARY, {
          params: filterParams(state),
        });
        if (requestId === summaryRequestId) {
          commit("SUMMARY_SUCCESS", data);
        }
      } catch (e) {
        if (requestId === summaryRequestId) {
          commit("SUMMARY_ERROR");
        }
      }
    },

    async loadTable({ state, commit }) {
      if (!state.group) {
        commit("CLEAR_TABLE");
        return;
      }

      const requestId = ++tableRequestId;
      commit("TABLE_REQUEST");
      try {
        const { data } = await axios.get(API_ALL_CLIENTS_PORTFOLIO, {
          params: {
            ...filterParams(state),
            group: state.group,
            page: state.page,
            per_page: state.perPage,
            sort_by: state.sortBy || undefined,
            sort_desc: state.sortDesc ? 1 : 0,
            search: state.search || undefined,
          },
        });
        if (requestId === tableRequestId) {
          commit("TABLE_SUCCESS", data);
        }
      } catch (e) {
        if (requestId === tableRequestId) {
          commit("TABLE_ERROR");
        }
      }
    },

    // Все строки текущей группы (для экспорта в Excel)
    async loadAllRows({ state }) {
      const { data } = await axios.get(API_ALL_CLIENTS_PORTFOLIO, {
        params: {
          ...filterParams(state),
          group: state.group,
          per_page: 0,
          sort_by: state.sortBy || undefined,
          sort_desc: state.sortDesc ? 1 : 0,
          search: state.search || undefined,
        },
      });
      return data.data.map(prepareRow);
    },

    async selectGroup({ state, commit, dispatch }, group) {
      if (group === state.group && state.tableStatus === "success") {
        return;
      }
      commit("SET_GROUP", group);
      commit("SET_PAGE", 1);
      commit("SET_SEARCH", "");
      await dispatch("loadTable");
    },

    async setService({ commit, dispatch }, serviceId) {
      commit("SET_SERVICE", serviceId);
      await dispatch("init", { keepGroup: true });
    },

    // Период добавления, КАМ, Sales менеджер
    async setFilters({ commit, dispatch }, filters) {
      commit("SET_FILTERS", filters);
      await dispatch("init", { keepGroup: true });
    },

    async setPage({ commit, dispatch }, page) {
      commit("SET_PAGE", page);
      await dispatch("loadTable");
    },

    async setPerPage({ commit, dispatch }, perPage) {
      commit("SET_PER_PAGE", perPage);
      commit("SET_PAGE", 1);
      await dispatch("loadTable");
    },

    async setSort({ commit, dispatch }, sort) {
      commit("SET_SORT", sort);
      commit("SET_PAGE", 1);
      await dispatch("loadTable");
    },

    async setSearch({ commit, dispatch }, search) {
      commit("SET_SEARCH", search);
      commit("SET_PAGE", 1);
      await dispatch("loadTable");
    },

    // После смены признака «В зоне риска»: обновить счётчики, а в группе «В зоне риска» - и список
    async riskChanged({ state, commit, dispatch }, payload) {
      commit("SET_RISK", payload);
      dispatch("loadSummary");
      if (state.group === "at_risk") {
        await dispatch("loadTable");
      }
    },
  },

  getters: {
    summaryLoading: (state) => state.summaryStatus === "loading",
    tableLoading: (state) => state.tableStatus === "loading",
    tableError: (state) => state.tableStatus === "error",
    tableData: (state) => state.rows,
    tableTitle: (state) => state.group,

    subcompanies(state) {
      return state.subcompanies.map((el) => {
        const orders = el.order_ids;
        return { ...el, ...orders };
      });
    },
  },
};

export default {
  ...clientPortfolio,
};
